import axios from "axios";
import { API_URL } from "../apiConfig";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token");
  return { Authorization: `Bearer ${token}` };
};

export const meetingMediaUrl = (fileUrl) => {
  if (!fileUrl) return "";
  if (/^(https?:|blob:|data:)/i.test(fileUrl)) return fileUrl;
  const base = String(API_URL || "").replace(/\/$/, "");
  return `${base}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
};

/**
 * Original / preview a tamaño completo: prioriza front de muestra si la foto
 * está linkada (displayFileUrl / muestraFrontUrl), si no el fileUrl.
 */
export const meetingPhotoDisplayUrl = (photo) => {
  if (!photo) return "";
  const raw =
    photo.displayFileUrl || photo.muestraFrontUrl || photo.fileUrl || "";
  return meetingMediaUrl(raw);
};

/**
 * Mosaico: prioriza thumb del back.
 * Sin thumb → fileUrl / blob (subida pendiente). No dejar huecos grises.
 * Vídeo sin thumb → vacío (el UI pone ▶).
 * El viewer sigue usando meetingPhotoDisplayUrl.
 */
export const meetingPhotoThumbUrl = (photo) => {
  if (!photo) return "";
  if (photo.thumbUrl) return meetingMediaUrl(photo.thumbUrl);
  const mime = String(photo.mimeType || "").toLowerCase();
  const video =
    photo.kind === "video" ||
    mime.startsWith("video/") ||
    /\/videos\//i.test(String(photo.fileUrl || ""));
  if (video) return "";
  // Blob local o archivo de la reunión (no el front de muestra, que se repite)
  const raw = photo.fileUrl || photo.displayFileUrl || "";
  if (!raw) return "";
  return meetingMediaUrl(raw);
};

/** Conserva vision/transcript ya cargados en preview tras un soft-refresh lite. */
export const mergeMeetingPhotoAiDetails = (incoming = [], previous = []) => {
  const prevById = new Map((previous || []).map((p) => [Number(p.id), p]));
  return (incoming || []).map((p) => {
    const old = prevById.get(Number(p.id));
    if (!old) return p;
    const vision =
      String(p.visionText || "").trim() || String(old.visionText || "").trim();
    const transcript =
      String(p.transcriptText || "").trim() ||
      String(old.transcriptText || "").trim();
    return {
      ...p,
      visionText: vision,
      transcriptText: transcript,
      hasVision:
        Boolean(p.hasVision) || Boolean(vision) || Boolean(old.hasVision),
      hasTranscript:
        Boolean(p.hasTranscript) ||
        Boolean(transcript) ||
        Boolean(old.hasTranscript),
    };
  });
};

export const getMeetingPhotoDetail = async (sessionId, photoId) => {
  const res = await axios.get(
    `${API_URL}/api/meeting-sessions/${sessionId}/photos/${photoId}`,
    { headers: authHeaders() },
  );
  return res.data;
};

export const listMeetingClientContacts = async (marca) => {
  const res = await axios.get(
    `${API_URL}/api/meeting-sessions/client-contacts`,
    {
      headers: authHeaders(),
      params: { marca: String(marca || "").trim() },
    },
  );
  return res.data;
};

/** Raíces del árbol: [{ customerName, sessionCount }]. */
export const listMeetingTreeClients = async () => {
  const res = await axios.get(`${API_URL}/api/meeting-sessions/tree-clients`, {
    headers: authHeaders(),
  });
  return res.data;
};

/**
 * Listado de sesiones.
 * @param {{ customerName?: string, q?: string }} [params]
 *   customerName: filtro por cliente ("" / omitido especial → sin cliente vía "__none__")
 *   q: búsqueda texto
 */
export const listMeetingSessions = async (params = {}) => {
  const query = {};
  if (params && Object.prototype.hasOwnProperty.call(params, "customerName")) {
    const key = String(params.customerName || "").trim();
    query.customerName = key ? key : "__none__";
  }
  if (params?.q != null && String(params.q).trim()) {
    query.q = String(params.q).trim();
  }
  const res = await axios.get(`${API_URL}/api/meeting-sessions`, {
    headers: authHeaders(),
    params: query,
  });
  return res.data;
};

export const getMeetingSession = async (id) => {
  const res = await axios.get(`${API_URL}/api/meeting-sessions/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
};

/** Segundo request: transcripts de audio + resumen (tras pintar la galería). */
export const getMeetingSessionTranscripts = async (id) => {
  const res = await axios.get(
    `${API_URL}/api/meeting-sessions/${id}/transcripts`,
    { headers: authHeaders() },
  );
  return res.data;
};

export const createMeetingSession = async (body = {}) => {
  const res = await axios.post(`${API_URL}/api/meeting-sessions`, body, {
    headers: authHeaders(),
  });
  return res.data;
};

export const patchMeetingSession = async (id, body) => {
  const res = await axios.patch(`${API_URL}/api/meeting-sessions/${id}`, body, {
    headers: authHeaders(),
  });
  return res.data;
};

export const stopMeetingSession = async (id) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/stop`,
    {},
    { headers: authHeaders() },
  );
  return res.data;
};

export const uploadMeetingAudioChunk = async (
  id,
  file,
  { seq, durationMs } = {},
) => {
  const form = new FormData();
  form.append("file", file);
  if (seq != null) form.append("seq", String(seq));
  if (durationMs != null) form.append("durationMs", String(durationMs));
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/audio-chunks`,
    form,
    { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } },
  );
  return res.data;
};

export const uploadMeetingPhoto = async (
  id,
  file,
  { caption, offsetMs, capturedAt } = {},
) => {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("caption", caption);
  if (offsetMs != null) form.append("offsetMs", String(offsetMs));
  if (capturedAt) form.append("capturedAt", capturedAt);
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/photos`,
    form,
    { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } },
  );
  return res.data;
};

export const patchMeetingPhoto = async (id, photoId, body) => {
  const res = await axios.patch(
    `${API_URL}/api/meeting-sessions/${id}/photos/${photoId}`,
    body,
    { headers: authHeaders() },
  );
  return res.data;
};

export const deleteMeetingPhoto = async (id, photoId) => {
  const res = await axios.delete(
    `${API_URL}/api/meeting-sessions/${id}/photos/${photoId}`,
    { headers: authHeaders() },
  );
  return res.data;
};

/** Mueve fotos/vídeos a otra reunión. */
export const moveMeetingPhotos = async (
  id,
  { targetMeetingSessionId, photoIds },
) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/photos/move`,
    { targetMeetingSessionId, photoIds },
    { headers: authHeaders() },
  );
  return res.data;
};

export const regenMeetingMedia = async (id, photoIds) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/photos/regen`,
    { photoIds },
    { headers: authHeaders(), timeout: 1200000 },
  );
  return res.data;
};

export const summarizeMeetingSession = async (id) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/summarize`,
    {},
    { headers: authHeaders(), timeout: 2700000 },
  );
  return res.data;
};

/** Escaneo en reunión: movimiento a cliente + Se queda en Cliente + leftWithClient. */
export const leaveMeetingSamples = async (
  id,
  { samples, ID_CLIENTE, ID_CONTACTO, PERSONA_ENVIO, NUM_COURIER, FECHA_ENVIO },
) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/leave-samples`,
    {
      samples,
      ID_CLIENTE,
      ID_CONTACTO,
      PERSONA_ENVIO,
      NUM_COURIER,
      FECHA_ENVIO,
    },
    { headers: authHeaders() },
  );
  return res.data;
};

/** Deshacer SE QUEDA EN CLIENTE (movimiento + foto leave + estado). */
export const unleaveMeetingSamples = async (id, { idMuestras }) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/unleave-samples`,
    { idMuestras },
    { headers: authHeaders() },
  );
  return res.data;
};

/** Quitar vínculo muestra ↔ media de la reunión. */
export const unlinkMeetingPhotosMuestras = async (
  id,
  { photoIds, idMuestra } = {},
) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/photos/unlink-muestras`,
    { photoIds, idMuestra },
    { headers: authHeaders() },
  );
  return res.data;
};

/** Escaneo en reunión: solo foto de la muestra en Sin agrupar (sin movimiento). */
export const importMeetingSamples = async (id, { samples }) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/import-samples`,
    { samples },
    { headers: authHeaders() },
  );
  return res.data;
};

export const createMeetingFreeNote = async (id, body) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/notes`,
    { body },
    { headers: authHeaders() },
  );
  return res.data;
};

export const patchMeetingFreeNote = async (id, noteId, body) => {
  const res = await axios.patch(
    `${API_URL}/api/meeting-sessions/${id}/notes/${noteId}`,
    { body },
    { headers: authHeaders() },
  );
  return res.data;
};

export const deleteMeetingFreeNote = async (id, noteId) => {
  await axios.delete(`${API_URL}/api/meeting-sessions/${id}/notes/${noteId}`, {
    headers: authHeaders(),
  });
};

export const claimMeetingRecording = async (id) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/recording/claim`,
    {},
    { headers: authHeaders() },
  );
  return res.data;
};

export const releaseMeetingRecording = async (id, { force = false } = {}) => {
  const res = await axios.post(
    `${API_URL}/api/meeting-sessions/${id}/recording/release`,
    { force },
    { headers: authHeaders() },
  );
  return res.data;
};

export const deleteMeetingSession = async (id) => {
  const res = await axios.delete(`${API_URL}/api/meeting-sessions/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
};
